import * as PIXI from 'pixi.js';
import isEqual from 'lodash/isEqual';
import type { RawAssetData, TranscriptData } from '../../video-data/types';
import type { BaseAssetSettings } from '../asset/BaseAsset';
import { TextAsset } from '../asset/TextAsset';
import { addAssetToContainer } from '../../libs/buildChildrenForScene';
import { Config } from '../../video-data/config';
import { assertInArray, assertString } from '../../validators';
import { BaseAsset } from '../asset/BaseAsset';
import type { DestroyOptions } from '../../types';
import { AspectRatio, ALL_ASPECT_RATIOS } from '../../aspectRatioConfig';
import { BaseGroup } from '../BaseGroup';
import { CaptionAsset } from '../asset/CaptionAsset/asset/CaptionAsset';
import { FancyCaptionAsset } from '../asset/FancyCaptionAsset/asset/FancyCaptionAsset';
import type { Transcript } from '../Transcript';
import { ErrorReporter } from '../../libs/error-reporter';
import type { TextDirection } from '../../enums';
import type Luminary from '../../index';

type CaptionsManagerSettings = BaseAssetSettings & {
    disableCaptions?: boolean;
};

type StyleCacheEntry = {
    assetData: RawAssetData;
    decorationsData: RawAssetData[];
};

/**
 * The CaptionsManager displays a scene group's captions using a CanvasCaptionAsset.
 * It manages the caption styles and the decoration workspace.
 * Decorations are additional assets on the caption style design that can be used as a background
 * for the captions.
 */
export class CaptionsManager extends BaseAsset {
    override _classType = 'CaptionsManager';

    /*
     * Often this will be the same asset, so need to create fresh copies each time.
     *
     * @internal
     */
    static _appliedAssetsCache = new Map<string, StyleCacheEntry>();

    // Where any background decorations are, e.g. a gradient behind the captions.
    #decorationsWorkspace: BaseGroup;
    #captionAsset: CaptionAsset | FancyCaptionAsset;
    #shouldLoadDefaultStyleAndDecorations: boolean = false;
    #aspectRatio: AspectRatio = AspectRatio.LANDSCAPE;
    #sceneDesignId: string | null = null;
    #disableCaptions: boolean = false;
    #userChanges: RawAssetData | null = null;

    // TODO(strict-ts): Change container type when BaseAsset.ts is updated
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(container: any, settings: CaptionsManagerSettings) {
        super(container, settings);
        if (settings.hasOwnProperty('disableCaptions')) {
            this.#disableCaptions = settings.disableCaptions!;
        }

        this.#decorationsWorkspace = new BaseGroup(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            timeline: this._globalTimeline,
            isFinalTopLevel: false,
            updateParentOnRedraw: true,
            name: 'decorationsWorkspace',
            assetNetworkManager: this._assetNetworkManager,
            getAspectRatio: () => this._getAspectRatio(),
            reusableTextures: this._reusableTextures,
            getReferenceResolution: () => this.getReferenceResolution(),
            getOutputResolution: () => this.getOutputResolution(),
        });

        this.insertAsset(this.#decorationsWorkspace);

        this.#captionAsset = new CaptionAsset(this, {
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            timeline: this._globalTimeline,
            isFinalTopLevel: false,
            updateParentOnRedraw: true,
            name: 'captionWorkspace',
            assetNetworkManager: this._assetNetworkManager,
            getAspectRatio: () => this._getAspectRatio(),
            reusableTextures: this._reusableTextures,
            getReferenceResolution: () => this.getReferenceResolution(),
            getOutputResolution: () => this.getOutputResolution(),
        });
        this.insertAsset(this.#captionAsset);
    }

    // Since the CaptionManager doesn't load any external resources such as urls, it should always load.
    // If we use the default implementation then we wouldn't build its children if fetchResources is false.
    override get _shouldLoad(): boolean {
        return true;
    }

    get transcript(): Transcript {
        return this.#captionAsset.transcript;
    }

    get asset(): CaptionAsset | FancyCaptionAsset {
        return this.#captionAsset;
    }

    set _aspectRatio(value: AspectRatio) {
        if (this.#aspectRatio === value) {
            return;
        }
        assertInArray(value, ALL_ASPECT_RATIOS);

        const sceneDesignId = this.#sceneDesignId;
        this.#userChanges = (() => {
            const previousAspectRatio = this.#aspectRatio;

            if (!sceneDesignId || !this._rootOrNull?.isTemplateBundleSet) {
                return null;
            }

            const currentAssetData = this.#captionAsset.saveEditable();
            const { editableAssetData: previousDefaultData } = CaptionsManager.#getSceneDesignData(
                this.root,
                sceneDesignId,
                previousAspectRatio,
            );

            const styleIsEqual = isEqual(currentAssetData?.style, previousDefaultData?.style);
            const textSizeIsEqual = currentAssetData.textSize === previousDefaultData.textSize;

            if (styleIsEqual && textSizeIsEqual) {
                return null;
            }

            return currentAssetData as RawAssetData;
        })();

        if (this.#userChanges) {
            const styleKey = `${sceneDesignId}_${value}_${this._rootOrNull?.textDirection ?? ''}`;
            CaptionsManager._appliedAssetsCache.delete(styleKey);
        }

        this.#aspectRatio = value;
        this.#shouldLoadDefaultStyleAndDecorations = true;
        this.requiresRedraw = true;
    }

    get _aspectRatio() {
        return this.#aspectRatio;
    }

    /**
     * The scene design id to use to style the captions.
     * This scene design must have sceneCategory of 'caption', and have only one text asset at the scene level that will
     * be used as a source of style.
     */
    get sceneDesignId(): string | null {
        return this.#sceneDesignId;
    }

    set sceneDesignId(sceneDesignId: string | null) {
        if (this.#sceneDesignId === sceneDesignId) {
            return;
        }

        assertString(sceneDesignId as string, true);
        this.#sceneDesignId = sceneDesignId;
        this.#shouldLoadDefaultStyleAndDecorations = true;
        this.requiresRedraw = true;
    }

    #removeDecorations() {
        if (this.#decorationsWorkspace.children.length === 0) return;
        this._batchRedraw(() => this.#decorationsWorkspace.clear());
    }

    override clear(): void {
        this.#removeDecorations();
        this.sceneDesignId = null;
    }

    /**
     * Sets the visibility of both the captions and decorations PIXI container for rendering purposes. If the
     * visibility is set to false then the caption managers content will not be rendered. If captions are globally
     * disabled through disableCaptions then visibility will be set to false regardless of the value passed in.
     **/
    _setCaptionsAndDecorationsVisibility(visible: boolean) {
        if (this.#disableCaptions) {
            visible = false;
        }
        this.#captionAsset.visible = visible;
        this.#decorationsWorkspace.visible = visible;
    }

    override _checkAndFixProperties() {
        this._setCaptionsAndDecorationsVisibility(this.visible);
    }

    protected override _build(): void {
        if (this.#disableCaptions) {
            return;
        }
        this.#loadCaptionStyleForSceneDesign();
    }

    override updateForRender() {
        super.updateForRender();
        this.#captionAsset.updateForRender();
    }

    /**
     * @deprecated use {@link visible} instead
     */
    override get visibleWithUpdate() {
        return super.visibleWithUpdate;
    }

    override set visibleWithUpdate(value: boolean) {
        if (this.visibleWithUpdate === value) {
            return;
        }
        super.visibleWithUpdate = value;
        // rebuild captions when visibility changes because when captions are invisible they aren't built
        this.#captionAsset.requiresRedraw = true;
    }

    override get requiresRedraw() {
        if (!this.isDoneTryingToLoad) {
            return true;
        }
        return super.requiresRedraw;
    }

    override set requiresRedraw(value) {
        super.requiresRedraw = value;
    }

    override get canPlay() {
        return this.children.every((asset) => asset.canPlay);
    }

    override get isLoaded() {
        return this.children.every((asset) => asset.isLoaded);
    }

    override get isDoneTryingToLoad() {
        return this.children.every((asset) => asset.isDoneTryingToLoad);
    }

    get #styleCacheKey() {
        if (!this.sceneDesignId) {
            throw new Error('A style cache key requires a valid sceneDesignId');
        }
        // this.root might not be set - perhaps in tests. It doesn't matter for the key.
        return `${this.sceneDesignId}_${this._aspectRatio}_${this._rootOrNull?.textDirection ?? ''}`;
    }

    /**
     * Returns the max text bounds of the caption asset if a transcript is set, otherwise returns
     * an empty rectangle.
     */
    override get approximateMaxBounds() {
        if (this.transcript.empty) {
            return PIXI.Rectangle.EMPTY;
        }
        return this.asset.approximateMaxBounds;
    }

    _decorationsCustomSave() {
        return this.#decorationsWorkspace.getAssets({ deep: false }).map((child) => child.save());
    }

    _decorationsCustomLoad(decorations: RawAssetData[]) {
        decorations.forEach((decoration) => {
            const asset = addAssetToContainer(
                this.#decorationsWorkspace as unknown as Parameters<typeof addAssetToContainer>[0],
                decoration.classType,
                {
                    id: decoration.id,
                    fetchResources: this.#disableCaptions ? false : this.fetchResources,
                },
            );
            asset.load(decoration);
        });
    }

    _assetCustomLoad(assetData: RawAssetData) {
        this.#shouldLoadDefaultStyleAndDecorations = false;
        this.#style = {};

        if (
            assetData.classType === Config.FANCY_CAPTION_ASSET_TYPE &&
            !Config.isFancyCaptionAsset(this.#captionAsset)
        ) {
            this.#captionAsset.destroy();
            this.#captionAsset = new FancyCaptionAsset(this, {
                autoDraw: this.autoDraw,
                fetchResources: this.fetchResources,
                timeline: this._globalTimeline,
                isFinalTopLevel: false,
                updateParentOnRedraw: true,
                name: 'captionWorkspace',
                id: assetData.id,
                assetNetworkManager: this._assetNetworkManager,
                getAspectRatio: () => this._getAspectRatio(),
                reusableTextures: this._reusableTextures,
                getReferenceResolution: () => this.getReferenceResolution(),
                getOutputResolution: () => this.getOutputResolution(),
            });
            this.insertAsset(this.#captionAsset);
        }
        this.#captionAsset.load(assetData);
    }

    #recreateCaptionAsset(
        playbackRegions: readonly (readonly [number, number])[],
        existingTranscript: TranscriptData,
    ) {
        this.#captionAsset.playbackRegions = playbackRegions;
        this.#captionAsset.transcript.transcriptData = existingTranscript;
        this.insertAsset(this.#captionAsset);
    }

    #loadTextAssetData(assetData: RawAssetData, overrides?: RawAssetData) {
        this.#shouldLoadDefaultStyleAndDecorations = false;
        this.#style = {};
        const classType = assetData.classType;
        const existingTranscript = this.#captionAsset.transcript.transcriptData;
        const playbackRegions = this.#captionAsset.playbackRegions;

        if (
            classType === Config.FANCY_CAPTION_ASSET_TYPE &&
            this.#captionAsset.classType !== Config.FANCY_CAPTION_ASSET_TYPE
        ) {
            this.#captionAsset.destroy();
            this.#captionAsset = new FancyCaptionAsset(this, {
                autoDraw: this.autoDraw,
                fetchResources: this.fetchResources,
                timeline: this._globalTimeline,
                isFinalTopLevel: false,
                updateParentOnRedraw: true,
                name: 'captionWorkspace',
                assetNetworkManager: this._assetNetworkManager,
                getAspectRatio: () => this._getAspectRatio(),
                reusableTextures: this._reusableTextures,
                getReferenceResolution: () => this.getReferenceResolution(),
                getOutputResolution: () => this.getOutputResolution(),
            });
            this.#recreateCaptionAsset(playbackRegions, existingTranscript);
        } else if (
            classType === Config.TEXT_ASSET_TYPE &&
            this.#captionAsset.classType !== Config.CAPTION_ASSET_TYPE
        ) {
            this.#captionAsset.destroy();
            this.#captionAsset = new CaptionAsset(this, {
                autoDraw: this.autoDraw,
                fetchResources: this.fetchResources,
                timeline: this._globalTimeline,
                isFinalTopLevel: false,
                updateParentOnRedraw: true,
                name: 'captionWorkspace',
                assetNetworkManager: this._assetNetworkManager,
                getAspectRatio: () => this._getAspectRatio(),
                reusableTextures: this._reusableTextures,
                getReferenceResolution: () => this.getReferenceResolution(),
                getOutputResolution: () => this.getOutputResolution(),
            });
            this.#recreateCaptionAsset(playbackRegions, existingTranscript);
        }

        this.#captionAsset.load(assetData, {});

        if (!overrides) {
            return;
        }

        this.#captionAsset.loadEditable(overrides);
    }

    _assetCustomSave() {
        return this.#captionAsset.save();
    }

    override get readonlyProperties(): string[] {
        return [];
    }

    override get propertiesToSerialize(): string[] {
        return [
            // our users can hide the captions, so we need to serialize the visibility
            'visible',
            'sceneDesignId',
            'style',
            'asset',
            'decorations',
        ];
    }

    override destroy(options?: DestroyOptions) {
        super.destroy(options);
    }

    _styleCustomSave(): null {
        return null;
    }

    /** @deprecated kept for backward compatibility */
    #style: object | null = null;

    /**
     * @deprecated
     * We use it now just for backward compatibility
     */
    get style(): object | null {
        return this.#style;
    }

    _styleCustomLoad(data: RawAssetData | undefined): void {
        if (data) {
            const container = {
                root: this.root,
                slide: {},
            };

            const textAsset = new TextAsset(container, {
                isFinalTopLevel: false,
                autoDraw: true,
                timeline: this._globalTimeline,
                fetchResources: false,
                assetNetworkManager: this._assetNetworkManager,
                getAspectRatio: () => this._getAspectRatio(),
                reusableTextures: this._reusableTextures,
                getReferenceResolution: () => this.getReferenceResolution(),
                getOutputResolution: () => this.getOutputResolution(),
            });

            textAsset.load(data);

            const textAssetData = textAsset.save();

            this.#captionAsset.load(textAssetData as RawAssetData, {});
        }
    }

    /**
     * @internal
     */
    _propagateTextDirection(textDirection: TextDirection) {
        if (!this.#captionAsset) {
            return;
        }

        for (const style of Object.values(this.#captionAsset.styles)) {
            style.textDirection = textDirection;
        }
    }

    #loadCaptionStyleForSceneDesign() {
        if (!this.root.isTemplateBundleSet) {
            // We cannot load the style from the template if we don't have the template bundle.
            // This is fine and expected when loading a video, since the previously set style is serialized and loaded again.
            return;
        }

        if (!this.#shouldLoadDefaultStyleAndDecorations) {
            return;
        }
        this.#shouldLoadDefaultStyleAndDecorations = false;

        this.#removeDecorations();
        for (const style of Object.values(this.#captionAsset.styles)) {
            style.removeDynamicStyles();
        }
        if (!this.sceneDesignId) {
            return;
        }

        const sceneDesignId = this.sceneDesignId;
        const aspectRatio = this._aspectRatio;
        const styleKey = this.#styleCacheKey;
        const cachedStyle = CaptionsManager._appliedAssetsCache.get(styleKey);

        if (cachedStyle) {
            this.#loadTextAssetData(cachedStyle.assetData);
            this._decorationsCustomLoad(cachedStyle.decorationsData);
            return;
        }

        const { assetData, decorationsData } = CaptionsManager.#getSceneDesignData(
            this.root,
            sceneDesignId,
            aspectRatio,
        );

        this.#loadTextAssetData(assetData, this.#userChanges ?? undefined);
        this._decorationsCustomLoad(decorationsData);
        this.#userChanges = null;

        CaptionsManager._appliedAssetsCache.set(styleKey, {
            assetData: assetData,
            decorationsData,
        });
    }

    static #getSceneDesignData(
        luminary: Luminary,
        sceneDesignId: string,
        aspectRatio: AspectRatio,
    ): {
        assetData: RawAssetData;
        editableAssetData: RawAssetData;
        decorationsData: RawAssetData[];
    } {
        return luminary._selectDataFromSceneDesign(sceneDesignId, (scene) => {
            scene.watermark.removeWatermarkIfExists();

            if (scene.sceneCategory !== Config.CATEGORY_CAPTION) {
                ErrorReporter.setContext('captions', { sceneDesignId, aspectRatio });
                throw new Error('Expecting scene design to be in the "caption" category');
            }
            const textAssets = scene.getAssetsByType(Config.TEXT_ASSET_TYPE, { deep: false });
            const fancyAssets = scene.getAssetsByType(Config.FANCY_CAPTION_ASSET_TYPE, {
                deep: false,
            });

            if (
                (!textAssets || textAssets.length !== 1) &&
                (!fancyAssets || fancyAssets.length !== 1)
            ) {
                ErrorReporter.setContext('captions', { sceneDesignId, aspectRatio });
                throw new Error(
                    'Expecting one text or fancy asset on the scene to use as a caption in scene design',
                );
            }

            if (textAssets.length > 0 && fancyAssets.length > 0) {
                ErrorReporter.setContext('captions', { sceneDesignId, aspectRatio });
                throw new Error(
                    'Expecting only ONE caption asset on the scene design, either text or fancy.',
                );
            }

            if (fancyAssets.length > 0) {
                const assetData = fancyAssets[0].save() as RawAssetData;
                const editableAssetData = fancyAssets[0].saveEditable() as RawAssetData;
                const decorationsData = scene
                    .getAssets({ deep: false })
                    .filter((child) => !Config.isFancyCaptionAsset(child))
                    .map((decoration) => decoration.save()) as RawAssetData[];

                return {
                    assetData,
                    editableAssetData,
                    decorationsData,
                };
            }

            const assetData = textAssets[0].save() as RawAssetData;
            const editableAssetData = textAssets[0].saveEditable() as RawAssetData;
            const decorationsData = scene
                .getAssets({ deep: false })
                .filter((child) => !Config.isTextAsset(child))
                .map((decoration) => decoration.save()) as RawAssetData[];

            return {
                assetData,
                editableAssetData,
                decorationsData,
            };
        });
    }
}
